import { useCallback } from 'react';

import { useTracker } from '@coursera/event-pulse/react';

import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type { PreviewVideoData } from 'bundles/unified-description-page-common/contexts/PreviewVideoExperimentContext';

export type PreviewVideoPlacement = 'hero' | 'syllabus';

export type PreviewVideoInteraction = 'play' | 'end' | 'mute' | 'unmute' | 'next_video_play' | 'exit';

export type PreviewVideoPlaybackMetadata = {
  muted?: boolean;
  videoPosition?: number;
  videoDuration?: number;
  progress?: number;
};

const SECTION_NAME_BY_PLACEMENT: Record<PreviewVideoPlacement, 'hero_banner' | 'syllabus'> = {
  hero: 'hero_banner',
  syllabus: 'syllabus',
};

/**
 * Event Pulse tracking for the XDP preview video experiments (MONETIZE-2876).
 */
export const usePreviewVideoTracking = (placement: PreviewVideoPlacement) => {
  const track = useTracker();
  const { product } = useEventingProductData();

  const trackPreviewVideoInteraction = useCallback<
    (interaction: PreviewVideoInteraction, video: PreviewVideoData, playback?: PreviewVideoPlaybackMetadata) => void
  >(
    (interaction, video, playback) => {
      track('interact_video_player', {
        videoPlayerInteractionType: interaction,
        videoPlayer: {
          videoId: video.id,
          videoName: video.name,
          previewVideoType: video.videoType,
          ...playback,
        },
        pageSection: { sectionName: SECTION_NAME_BY_PLACEMENT[placement] },
        ...(product && { product }),
      });
    },
    [track, product, placement]
  );

  const trackOpenPreviewVideo = useCallback<(video: PreviewVideoData) => void>(
    (video) => {
      track('click_button', {
        button: { name: 'open_preview_video' },
        videoPlayer: {
          videoId: video.id,
          videoName: video.name,
          previewVideoType: video.videoType,
        },
        pageSection: { sectionName: SECTION_NAME_BY_PLACEMENT[placement] },
        ...(product && { product }),
      });
    },
    [track, product, placement]
  );

  return { trackPreviewVideoInteraction, trackOpenPreviewVideo };
};
